import { useEffect, useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { useMyPermissions, useSettings, useUpdateSettings } from '@/hooks/use-settings'
import { COMPANY_FIELDS } from './settings-utils'
import { OrgReferralCard } from '@/pages/affiliate/org-referral-card'

export function CompanyTab() {
  const { data, isLoading, isError } = useSettings()
  const update = useUpdateSettings()
  const { t } = useTranslation('settings')
  // Quản lý / Nhân viên mặc định chỉ có `settings.view` → chỉ xem, không hiện nút Lưu (PUT /settings đòi `settings.update`).
  const canEdit = !!useMyPermissions().data?.has('settings.update')
  const [form, setForm] = useState<Record<string, string>>({})

  // Nạp giá trị hiện có vào form khi có dữ liệu.
  useEffect(() => {
    if (!data) return
    const next: Record<string, string> = {}
    for (const f of COMPANY_FIELDS) next[f.key] = data.settings[f.key] ?? ''
    setForm(next)
  }, [data])

  if (isLoading) return <Loading label={t('settings:company.loading')} />
  if (isError || !data) return <ErrorState />

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canEdit) return
    const entries = COMPANY_FIELDS.map((f) => ({ key: f.key, value: form[f.key] ?? '' }))
    try {
      await update.mutateAsync(entries)
      toast.success(t('settings:company.saved'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>{t('settings:company.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {COMPANY_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] ?? ''}
                  readOnly={!canEdit}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          {canEdit ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? <Loader2 className="animate-spin" /> : <Save />}
                {t('settings:company.save')}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('settings:company.readOnlyHint')}</p>
          )}
        </form>
      </CardContent>
    </Card>
    {/* Tiếp thị liên kết: chủ tổ chức nhập mã người giới thiệu (N ngày đầu) */}
    <OrgReferralCard />
    </div>
  )
}
